import { ClearOutlined, PictureOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { MouseEvent, TouchEvent, useEffect, useRef, useState } from 'react';

interface SignaturePadProps {
  valueSignature?: string;
  canUploadSignature?: boolean;
  onSignatureDrawn: (dataUrl: string) => void;
}

const SignaturePad: React.FC<SignaturePadProps> = ({ valueSignature, canUploadSignature, onSignatureDrawn }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const drawingRef = useRef<boolean>(false);
  const [scrollDisabled, setScrollDisabled] = useState<boolean>(false);

  const clearCanvasElement = () => {
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    ctx?.beginPath();
  };

  const loadImage = (imageSrc: string) => {
    const image = new Image();
    image.onload = () => {
      if (!canvasRef.current) {
        return;
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
    };
    image.src = imageSrc;
  };

  useEffect(() => {
    if (valueSignature) {
      loadImage(valueSignature);
    } else {
      clearCanvasElement();
    }
  }, [valueSignature]);

  useEffect(() => {
    const mainElement = document.querySelector('main') || document.querySelector('div');

    const updateOverflow = () => {
      if (mainElement) {
        mainElement.style.overflow = scrollDisabled ? 'hidden' : '';
      }
      document.body.style.overflow = scrollDisabled ? 'hidden' : '';
    };

    updateOverflow();

    return () => {
      if (mainElement) {
        mainElement.style.overflow = '';
      }
      document.body.style.overflow = '';
    };
  }, [scrollDisabled]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      loadImage(dataUrl);
      onSignatureDrawn(dataUrl);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const getCoordinates = (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const point = 'touches' in event ? event.touches[0] || event.changedTouches[0] : event;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (point.clientX - rect.left) * scaleX,
      y: (point.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    if (!canvasRef.current) {
      return;
    }

    const { x, y } = getCoordinates(event);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = '#5068aa';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
    }

    drawingRef.current = true;
  };

  const endDrawing = (event?: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    event?.preventDefault();
    if (!drawingRef.current) {
      return;
    }

    drawingRef.current = false;
    if (canvasRef.current) {
      onSignatureDrawn(canvasRef.current.toDataURL());
    }
  };

  const draw = (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    if (!drawingRef.current || !canvasRef.current) {
      return;
    }

    const { x, y } = getCoordinates(event);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const clearCanvas = () => {
    drawingRef.current = false;
    clearCanvasElement();
    onSignatureDrawn('');
  };

  const toggleScroll = () => {
    setScrollDisabled((prev) => !prev);
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={400}
        height={200}
        style={{ width: '100%', border: '1px solid #d9d9d9', cursor: 'crosshair', touchAction: 'none' }}
        onTouchStart={startDrawing}
        onTouchEnd={endDrawing}
        onTouchCancel={endDrawing}
        onTouchMove={draw}
        onMouseDown={startDrawing}
        onMouseUp={endDrawing}
        onMouseLeave={endDrawing}
        onMouseMove={draw}
      />
      <Button icon={<ClearOutlined />} onClick={clearCanvas}>
        Reseteaza
      </Button>
      {canUploadSignature && (
        <>
          <Button icon={<PictureOutlined />} onClick={() => fileInputRef.current?.click()} style={{ marginLeft: '20px' }}>
            Incarca semnatura
          </Button>
          <input type='file' accept='image/*' style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileChange} />
        </>
      )}
      <Button onClick={toggleScroll} style={{ marginLeft: '20px' }}>
        {scrollDisabled ? 'Start Scroll' : 'Stop Scroll'}
      </Button>
    </>
  );
};

export default SignaturePad;
